import React, { useState, ReactElement } from 'react';
import styled from 'styled-components';
import { Redirect } from 'react-router-dom';
import history from 'lib/history';
import colors from 'theme/colors';
import routes from 'router/routes';
import ModalBasic from 'components/modal-provider/modal-basic';
import { ModalButton, ModalDeleteButton, ModalCancelButton } from 'components/modal-provider/modal';
import UrlShare from 'components/url-share';
import messages from 'constants/messages';
import labels from 'constants/labels';
import { fromApi } from 'lib/helpers/deal-formatters';
import { IDeal, ESearchFilter } from 'store/deals/types';
import { updateDealWithAllProduct } from 'pages/compare/default-view/helpers';
import { IFieldOption } from 'store/field-options/types';
import { Row } from 'react-table';

const { disabledButton, primary, lightBackgroundBase, fontBody, tableActionRed } = colors;

const Container = styled.div`
  height: 50px;
  margin-top: 8px;
  background-color: rgba(28, 115, 255, 0.1);
  padding-left: 16px;
  font-size: 14px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;

  div {
    font-weight: 600;
    padding-right: 28px;
    display: flex;
    align-items: center;
  }
`;

const buttonType = (isHover: boolean) => {
  return isHover ? primary : fontBody;
};

const actionType = (action: string | undefined, isHover: boolean) => {
  return action === 'delete' ? tableActionRed : buttonType(isHover);
};

const fontWeightLow = 400;
const fontWeightHigh = 600;
const minWidth = 93;

const Button = styled.button<{ action?: string }>`
  background-color: ${props => (props.action === 'compare' ? primary : 'transparent')};
  border: none;
  border-radius: 3px;
  color: ${props => (props.action === 'compare' ? lightBackgroundBase : actionType(props.action, false))};
  font-size: 14px;
  font-weight: ${props => (props.action === 'compare' ? fontWeightHigh : fontWeightLow)};
  height: 34px;
  margin: 8px 28px 8px 0px;
  min-width: ${props => (props.action === 'compare' ? minWidth : 0)}px;
  opacity: 1;
  transition: opacity 0.2s ease;

  :hover {
    opacity: 0.8;
    cursor: pointer;
    font-weight: 600;
    color: ${props => (props.action === 'compare' ? lightBackgroundBase : actionType(props.action, true))};
  }

  :disabled {
    color: ${disabledButton};
    pointer-events: none;
  }
`;

export interface ITableActionProps {
  addDealsFromDashboard: (deals: IDeal[]) => void;
  clearModalContent: () => void;
  deleteMultipleDeals: (dealIds: (string | undefined)[]) => void;
  evaluateDeals: () => void;
  selectedRows: Row<IDeal>[];
  setModalContent: (content: ReactElement) => void;
  availableProducts: IFieldOption[] | undefined;
  searchFilter: ESearchFilter;
  setCompareDealIds: (compareDealIds: (string | undefined)[]) => void;
}

const TableActions = ({
  addDealsFromDashboard,
  clearModalContent,
  deleteMultipleDeals,
  evaluateDeals,
  selectedRows,
  setModalContent,
  availableProducts,
  searchFilter,
  setCompareDealIds,
}: ITableActionProps) => {
  const [dealToDuplicate, setDealToDuplicate] = useState<null | IDeal>(null);
  const statusToDuplicate = '';

  /**
   * Share is available when (1) is selected
   * Duplicate is available when (1) is selected
   * Delete is available when (1+) is selected
   * Compare is available when (1-5) is selected
   */
  function calculateDisabledState(field: string) {
    if (selectedRows.length === 1) {
      return null;
    }

    if (field === 'compare' && selectedRows.length <= labels.comparisonLimit) {
      return null;
    }

    if (field === 'share' && selectedRows.length <= labels.comparisonLimit) {
      return null;
    }

    if (field === 'delete') {
      return null;
    }

    return { disabled: true };
  }

  function handleShare() {
    const dealIds = selectedRows.map((selectedRow: Row<IDeal>) => selectedRow.original.id);
    const dealsUrl =
      dealIds.length === 1
        ? `${window.location.href}deals/${selectedRows[0].original.id}`
        : `${window.location.href}compare/${dealIds.join()}`;
    const modalContent = (
      <>
        <p>{messages.info.shareDeal}</p>
        <UrlShare url={dealsUrl} />
        <br />
      </>
    );

    const modalButton = <ModalButton onClick={clearModalContent}>{labels.button.close}</ModalButton>;

    const modal = (
      <ModalBasic
        title={labels.titles.modals.shareDeal}
        content={modalContent}
        onClose={clearModalContent}
        buttons={modalButton}
      />
    );

    setModalContent(modal);
  }

  function handleDuplicate() {
    const deal = { ...selectedRows[0].original };
    delete deal.id;
    deal.status = statusToDuplicate;
    setDealToDuplicate(deal);
    clearModalContent();
  }

  function showDuplicateModal() {
    const modalContent = (
      <>
        <p>{messages.warnings.duplicateDealConfirmation}</p>
        <br />
      </>
    );

    const modalButtons = (
      <>
        <ModalCancelButton onClick={clearModalContent}>{labels.button.close}</ModalCancelButton>
        <ModalButton onClick={handleDuplicate}>{labels.button.duplicate}</ModalButton>
      </>
    );

    const modal = (
      <ModalBasic
        title={labels.titles.modals.duplicateDeal}
        content={modalContent}
        onClose={clearModalContent}
        buttons={modalButtons}
      />
    );

    setModalContent(modal);
  }

  function handleDelete() {
    const ids = selectedRows.map(row => row.original.id);
    const handleModalDeleteButton = () => {
      clearModalContent();
      if (ids !== undefined) {
        deleteMultipleDeals(ids);
      }
    };

    const deleteButtons = (
      <>
        <ModalCancelButton data-testid="tid-modal-cancel-button" onClick={clearModalContent}>
          {labels.button.cancel}
        </ModalCancelButton>
        <ModalDeleteButton data-testid="tid-modal-delete-button" onClick={handleModalDeleteButton}>
          {labels.button.deleteConfirmation}
        </ModalDeleteButton>
      </>
    );

    const modalContent = <p>{messages.warnings.deleteDealConfirmation}</p>;

    const modal = (
      <ModalBasic
        title={labels.titles.modals.deleteDeal}
        content={modalContent}
        onClose={clearModalContent}
        buttons={deleteButtons}
      />
    );

    setModalContent(modal);
  }

  // Extract deals, update local storage, and open Compare page
  function handleCompare() {
    const dealsToCompare = selectedRows.map((row: Row<IDeal>) => fromApi(row.original));
    setCompareDealIds(dealsToCompare.map((deal: IDeal) => deal.id));
    const dealsWithProductName = dealsToCompare.map((deals: IDeal) =>
      updateDealWithAllProduct(deals, availableProducts)
    );
    addDealsFromDashboard(dealsWithProductName);
    const dealIds = selectedRows.map(selectedRow => selectedRow.original.id);
    history.push(`compare/${dealIds.join()}`);
    evaluateDeals();
  }

  if (dealToDuplicate) {
    return (
      <Redirect
        to={{
          pathname: routes.dealCreate,
          state: {
            dealToDuplicate,
          },
        }}
      />
    );
  }

  if (!selectedRows || selectedRows.length === 0) {
    return null;
  }

  return (
    <Container>
      <div>Actions</div>
      <Button
        data-testid="tid-compare-button"
        action="compare"
        type="button"
        {...calculateDisabledState('compare')}
        onClick={handleCompare}
      >
        {labels.button.compare}
      </Button>
      <Button data-testid="tid-share-button" type="button" {...calculateDisabledState('share')} onClick={handleShare}>
        {labels.button.share}
      </Button>
      <Button
        data-testid="tid-duplicate-button"
        type="button"
        {...calculateDisabledState('duplicate')}
        onClick={showDuplicateModal}
      >
        {labels.button.duplicate}
      </Button>
      {ESearchFilter.MINE === searchFilter && (
        <Button
          action="delete"
          data-testid="tid-delete-button"
          type="button"
          {...calculateDisabledState('delete')}
          onClick={handleDelete}
        >
          {labels.button.delete}
        </Button>
      )}
    </Container>
  );
};

export default TableActions;
